import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { X, BookOpen, Quote, Calendar, Bookmark, Hash, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import { sanitizeChatMarkdown } from '@/utils/chatMarkdownSanitizer';

// Reusing the nice card layout from TextbookViewer
export default function TeacherNotePreviewModal({ note, isOpen, onClose, isDark, onRepairMath, repairingMath }) {
    if (!note) return null;

    const structuredContent = note.structured_content;
    const hasStructure = structuredContent && structuredContent.sections && structuredContent.sections.length > 0;

    const stripHtmlToText = (html) => {
        if (!html) return '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return (tempDiv.textContent || tempDiv.innerText || '').trim();
    };

    const extractLatexSpans = (text) => {
        if (!text) return [];
        const spans = [];
        const s = String(text);
        let i = 0;
        while (i < s.length) {
            const ch = s[i];
            // Skip escaped dollars
            if (ch === '\\' && s[i + 1] === '$') {
                i += 2;
                continue;
            }

            // Display math: $$...$$
            if (ch === '$' && s[i + 1] === '$') {
                const start = i + 2;
                const end = s.indexOf('$$', start);
                if (end !== -1) {
                    const latex = s.slice(start, end).trim();
                    if (latex) spans.push({ latex, display: true });
                    i = end + 2;
                    continue;
                }
            }

            // Inline math: $...$
            if (ch === '$') {
                const start = i + 1;
                let end = start;
                while (end < s.length) {
                    if (s[end] === '\\' && s[end + 1] === '$') {
                        end += 2;
                        continue;
                    }
                    if (s[end] === '$') break;
                    end += 1;
                }
                if (end < s.length && s[end] === '$') {
                    const latex = s.slice(start, end).trim();
                    if (latex) spans.push({ latex, display: false });
                    i = end + 1;
                    continue;
                }
            }

            i += 1;
        }
        return spans;
    };

    const katexFailures = useMemo(() => {
        const parts = [];
        if (hasStructure && structuredContent?.sections?.length) {
            for (const section of structuredContent.sections) {
                if (section?.heading) parts.push(sanitizeChatMarkdown(section.heading));
                if (Array.isArray(section?.points)) {
                    for (const point of section.points) {
                        if (point) parts.push(sanitizeChatMarkdown(point));
                    }
                }
            }
        } else {
            const fallback = sanitizeChatMarkdown(stripHtmlToText(note.content) || '');
            if (fallback) parts.push(fallback);
        }

        const spans = parts.flatMap(extractLatexSpans);
        const failures = [];
        for (const span of spans) {
            try {
                katex.renderToString(span.latex, {
                    throwOnError: true,
                    strict: false,
                    displayMode: !!span.display,
                });
            } catch (e) {
                failures.push({ ...span, error: e?.message || 'KaTeX render error' });
            }
        }
        return failures;
    }, [hasStructure, structuredContent, note?.content]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={cn("max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden border-none shadow-2xl", isDark ? "bg-[#1a1b1e]" : "bg-[#fcfaf5]")}>

                {/* Header */}
                <div className={cn("p-6 flex items-start justify-between border-b shrink-0", isDark ? "border-gray-800 bg-[#141517]" : "border-[#E0D4BF] bg-[#F5F1EA]")}>
                    <div className="flex bg-transparent space-x-4 pr-10">
                        <div className="mt-1 p-2 bg-gradient-to-br from-[hsl(var(--teal-vivid))] to-[hsl(var(--green-bright))] rounded-lg shadow-lg shrink-0">
                            <BookOpen className="w-6 h-6 text-[#1a1b1e]" />
                        </div>
                        <div>
                            <DialogTitle className={cn("text-2xl font-bold leading-tight mb-2", isDark ? "text-white" : "text-[#2B2B2B]")}>
                                {note.title}
                            </DialogTitle>
                            <div className="flex flex-wrap items-center gap-3 text-sm opacity-80">
                                <span className={cn("flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border", isDark ? "bg-accent/10 border-accent/20 text-accent-foreground" : "bg-[#E0D4BF] border-[#D4C4A8] text-[#5C4B30]")}>
                                    <Calendar className="w-3 h-3" />
                                    {new Date(note.created_at || Date.now()).toLocaleDateString()}
                                </span>
                                {note.chapter?.chapter_name && (
                                    <span className={cn("flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border", isDark ? "bg-accent/10 border-accent/20 text-accent-foreground" : "bg-[#E0D4BF] border-[#D4C4A8] text-[#5C4B30]")}>
                                        <Hash className="w-3 h-3" />
                                        {note.chapter.chapter_name}
                                    </span>
                                )}
                            </div>

                            {katexFailures.length > 0 && (
                                <div className={cn(
                                    "mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs",
                                    isDark ? "border-yellow-700/40 bg-yellow-500/10 text-yellow-200" : "border-yellow-300 bg-yellow-50 text-yellow-800"
                                )}>
                                    <AlertTriangle className="w-4 h-4" />
                                    {katexFailures.length} equation{katexFailures.length === 1 ? '' : 's'} may not render correctly. Try “Auto-fix equations”.
                                </div>
                            )}
                        </div>
                    </div>
                    <Button
                        onClick={onClose}
                        variant="ghost"
                        className={cn("absolute right-4 top-4 h-8 w-8 p-0 rounded-full hover:bg-black/10", isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-black")}
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                {/* Content - Scrollable */}
                <ScrollArea className="flex-1 w-full p-6 sm:p-8">
                    <div className="max-w-3xl mx-auto space-y-8 pb-10">
                        {hasStructure ? (
                            /* Structured Content View (Cards) */
                            <div className="space-y-8">
                                {structuredContent.overview && (
                                    <div className={cn("text-lg leading-relaxed italic border-l-4 pl-4 py-2", isDark ? "text-gray-300 border-[hsl(var(--teal-vivid))]" : "text-gray-700 border-[hsl(var(--teal-vivid))]")}>
                                        {structuredContent.overview}
                                    </div>
                                )}

                                {structuredContent.sections.map((section, sectionIdx) => (
                                    <div key={sectionIdx} className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700" style={{ animationDelay: `${sectionIdx * 100}ms` }}>
                                        {section.heading && (
                                            <div className="flex items-center justify-between gap-2 mb-4 mt-8 first:mt-0">
                                                <h2 className={cn("text-xl font-bold flex items-center gap-2", isDark ? "text-[hsl(var(--teal-vivid))]" : "text-[#2C5F2D]")}>
                                                    <Bookmark className="w-5 h-5 fill-current opacity-50" />
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                                        rehypePlugins={[rehypeKatex]}
                                                    >
                                                        {sanitizeChatMarkdown(section.heading)}
                                                    </ReactMarkdown>
                                                </h2>
                                                <div className={cn("h-[1px] flex-1 ml-4 opacity-30", isDark ? "bg-[hsl(var(--teal-vivid))]" : "bg-[#2C5F2D]")}></div>
                                            </div>
                                        )}

                                        <div className="grid gap-4">
                                            {section.points.map((point, pointIdx) => (
                                                <Card
                                                    key={pointIdx}
                                                    className={cn(
                                                        "border-none shadow-sm transition-all duration-300 group hover:shadow-md",
                                                        isDark ? "bg-[#25262b] hover:bg-[#2c2d32]" : "bg-white hover:bg-gray-50"
                                                    )}>
                                                    <CardContent className="p-5 flex gap-4">
                                                        {/* Bullet / Number */}
                                                        <div className={cn(
                                                            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-sm mt-0.5 transition-colors group-hover:scale-110",
                                                            isDark ? "bg-[#1a1b1e] text-[hsl(var(--teal-vivid))]" : "bg-[#F5F1EA] text-[#2C5F2D]"
                                                        )}>
                                                            {pointIdx + 1}
                                                        </div>

                                                        {/* Content */}
                                                        <div className={cn("flex-1 text-[15px] leading-relaxed [&_strong]:font-semibold [&_.katex]:text-foreground", isDark ? "text-gray-300 [&_strong]:text-white" : "text-gray-700 [&_strong]:text-black")}>
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                                                rehypePlugins={[rehypeKatex]}
                                                            >
                                                                {sanitizeChatMarkdown(point)}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {structuredContent.summary && (
                                    <div className={cn("mt-12 p-6 rounded-xl border border-dashed", isDark ? "bg-accent/5 border-accent/20" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                                        <h3 className={cn("text-lg font-bold mb-3 flex items-center gap-2", isDark ? "text-[hsl(var(--teal-vivid))]" : "text-[#2C5F2D]")}>
                                            <Quote className="w-5 h-5" /> Summary
                                        </h3>
                                        <p className={cn("leading-relaxed", isDark ? "text-gray-400" : "text-gray-700")}>
                                            {structuredContent.summary}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Fallback: Raw HTML (Manual Notes or Unstructured) */
                            <div className={cn(
                                "prose max-w-none text-[15px] leading-relaxed",
                                isDark ? "prose-invert" : "text-[#2B2B2B]",
                                "[&_a]:text-[hsl(var(--green-bright))] [&_a]:hover:text-[hsl(var(--teal-vivid))] [&_a]:underline [&_a]:underline-offset-2",
                                "[&_strong]:font-semibold [&_.katex]:text-foreground"
                            )}>
                                <ReactMarkdown
                                    remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                    rehypePlugins={[rehypeKatex]}
                                >
                                    {sanitizeChatMarkdown(stripHtmlToText(note.content) || 'No content available')}
                                </ReactMarkdown>
                            </div>
                        )}
                    </div>
                </ScrollArea>

                {/* Footer */}
                <div className={cn("p-4 border-t flex justify-end gap-3", isDark ? "border-gray-800 bg-[#141517]" : "border-[#E0D4BF] bg-[#F5F1EA]")}>
                    {typeof onRepairMath === 'function' && (
                        <Button
                            variant="outline"
                            onClick={onRepairMath}
                            disabled={!!repairingMath}
                            className={cn(
                                "gap-2",
                                isDark ? "border-gray-700 hover:bg-gray-800" : ""
                            )}
                            title="Attempts to clean up OCR artifacts and repair LaTeX equations"
                        >
                            {repairingMath ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            Auto-fix equations
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose} className={isDark ? "border-gray-700 hover:bg-gray-800" : ""}>
                        Close Preview
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
